// ตัวช่วยแสดงผลตารางสอนภาคศาสนา — ใช้ร่วมกันระหว่างหน้าจัดการตารางสอนของฝ่าย
// การสอน (pos 53) กับหน้าตารางสอนห้องเรียนของครูประจำชั้น (pos 51) ทั้งสองหน้าอ่าน
// ตารางชุดเดียวกันจาก is_teachtime จึงต้องเขียนรหัสวิชา/ชื่อครูออกมาแบบเดียวกัน

export const DAYS = [0, 1, 2, 3, 4, 5, 6];

// ชื่อวันบนเอกสารที่สั่งพิมพ์ — เป็นภาษาไทยเสมอ ไม่ตามภาษาเมนู เพราะเป็นเอกสาร
// ที่เข้าแฟ้ม (ชื่อวันบนหน้าจอใช้ t.days ของภาษาที่เลือก)
export const TH_DAYS = ["อาทิตย์", "จันทร์", "อังคาร", "พุธ", "พฤหัสบดี", "ศุกร์", "เสาร์"];

// ชื่อครูตามภาษาเมนู — เมนูยาวีใช้ชื่ออาหรับ ถ้าคนนั้นยังไม่ได้กรอกไว้ในทะเบียน
// บุคลากรก็ตกมาที่ชื่อไทย (ดีกว่าเว้นว่างจนไม่รู้ว่าใครสอน) ส่งคืน arabic มาด้วย
// เพื่อให้ตัวอักษรอาหรับได้คลาส arabic-text ส่วนชื่อไทยที่ตกมาไม่ได้
export const teacherLabel = (lang, row) => {
  const arabic = lang !== "th" && !!row?.t_arabicname;
  return { name: (arabic ? row.t_arabicname : row?.t_name) || "", arabic };
};

// ชื่อครูในช่องเลือกครู — แสดงทั้งสองชื่อเสมอ ภาษาของเมนูขึ้นก่อน
//
// ต่างจากการ์ดในตารางที่มีที่ว่างจำกัดจึงแสดงชื่อเดียว: ตอนเลือกครูจากรายชื่อทั้งภาค
// ต้องเทียบกับสิ่งที่คนเลือกจำได้ ซึ่งครูบางคนจำเป็นชื่อไทย บางคนจำเป็นชื่ออาหรับ
export const teacherOptionLabel = (lang, row) => {
  const { name } = teacherLabel(lang, row);
  const other = lang === "th" ? row?.t_arabicname : row?.t_name;
  const alt = String(other || "").trim();
  return alt && alt !== name ? `${name} — ${alt}` : name;
};

// รหัสวิชาทั้งสองภาคเรียน (isid1/isid2) — วิชาเดียวกันใช้คนละรหัสในแต่ละภาค
// ครูจึงต้องเห็นทั้งคู่ วิชาที่ยังไม่ได้ตั้งรหัสภาค 2 แสดงเฉพาะภาค 1
export const subjectCode = (row) =>
  [row?.isubject_isid1, row?.isubject_isid2].filter(Boolean).join(" / ");

// แถวแบนจาก timetableByRoom → grid[วัน][period_id] — แถวที่ยังไม่ลงคาบ
// (is_teachtime_id เป็น NULL เพราะ LEFT JOIN) ตกไป
export const buildGrid = (rows) => {
  const grid = {};
  rows.forEach((row) => {
    if (row.is_teachtime_id === null || row.teach_day === null || !row.period_id) return;
    if (!grid[row.teach_day]) grid[row.teach_day] = {};
    grid[row.teach_day][row.period_id] = row;
  });
  return grid;
};

// ตารางของครูหนึ่งคนเก็บเป็น "รายการ" ต่อช่อง ไม่ใช่แถวเดียวเหมือนตารางห้อง —
// ครูที่ถูกจัดซ้อนสองห้องในคาบเดียวต้องเห็นทั้งคู่ ไม่ใช่ให้ห้องหลังทับห้องแรกหายไป
// เพราะนั่นคือสิ่งที่หน้านี้มีไว้ให้จับ
export const buildTeacherGrid = (rows) => {
  const grid = {};
  rows.forEach((row) => {
    if (row.teach_day === null || !row.period_id) return;
    if (!grid[row.teach_day]) grid[row.teach_day] = {};
    (grid[row.teach_day][row.period_id] ||= []).push(row);
  });
  return grid;
};

// เวลาคาบจาก API เป็น HH:MM:SS — ตารางแสดงแค่ชั่วโมงกับนาที
export const shortTime = (v) => String(v || "").slice(0, 5);

// ─── เอกสารตารางสอน (PDF / สั่งพิมพ์) ────────────────────────────────────────
//
// เอกสารตามภาษาเมนู: เมนูไทยได้ใบภาษาไทยเรียงซ้ายไปขวา เมนูยาวีได้ใบยาวีเรียงขวา
// ไปซ้าย — ใบที่วางขวาไปซ้ายแต่ข้อความเป็นไทยจะอ่านยากกว่าเดิม ทิศทางกับภาษาจึง
// ต้องมาด้วยกัน ไม่ใช่เลือกแยกกัน
//
// cellHtml(day, period) คืนเนื้อในช่องเอง เพราะแต่ละหน้าบอกคนละเรื่อง (ตารางห้อง
// บอกว่าใครสอน ตารางครูบอกว่าสอนห้องไหน)
export const docOptions = (lang, t, section) =>
  lang === "th"
    ? { rtl: false, dayNames: TH_DAYS, dayHeader: "วัน", periodLabel: (n) => `คาบ ${n}`, emptyLabel: "ว่าง" }
    : {
        rtl: true,
        dayNames: t.days,
        dayHeader: section.colDay,
        periodLabel: section.periodNo,
        emptyLabel: section.empty,
      };

export const timetableHtml = ({
  title, subtitle, periods, cellHtml,
  rtl = false,
  dayNames = TH_DAYS,
  dayHeader = "วัน",
  periodLabel = (n) => `คาบ ${n}`,
  emptyLabel = "ว่าง",
}) => {
  const periodHeaders = periods.map(
    (p) => `<th style="background:#4472c4;color:#fff;padding:6px 4px;font-size:12px;text-align:center;border:1px solid #ccc;">${periodLabel(p.period_order)}<br/><span style="font-size:10px;font-weight:normal;direction:ltr;unicode-bidi:isolate;">${p.period_begin}–${p.period_end}</span></th>`
  ).join("");

  const bodyRows = DAYS.map((d) => {
    const cells = periods.map((p) => {
      const inner = cellHtml(d, p) || `<div style="color:#bbb;font-size:10px;">${emptyLabel}</div>`;
      return `<td style="padding:4px;border:1px solid #ccc;vertical-align:top;">${inner}</td>`;
    }).join("");
    return `<tr>
      <td style="padding:6px 8px;font-weight:700;font-size:12px;background:#f2f2f2;border:1px solid #ccc;white-space:nowrap;">${dayNames[d]}</td>
      ${cells}
    </tr>`;
  }).join("");

  // ฟอนต์อาหรับต้องมาก่อน Tahoma ในใบยาวี ไม่งั้นตัวอักษรอาหรับจะถูกเรนเดอร์ด้วย
  // ฟอนต์ไทยที่ครอบตัวอาหรับไว้แบบบีบๆ อ่านยาก
  const font = rtl
    ? "'Amiri','Scheherazade New','Traditional Arabic',Tahoma,serif"
    : "Tahoma,Arial,sans-serif";

  return `
    <div style="font-family:${font};padding:20px;background:#fff;color:#111;direction:${rtl ? "rtl" : "ltr"};text-align:${rtl ? "right" : "left"};">
      <h3 style="margin:0 0 4px;font-size:15px;">${title}</h3>
      <div style="margin-bottom:10px;font-size:12px;color:#555;">${subtitle}</div>
      <table style="border-collapse:collapse;width:100%;">
        <thead>
          <tr>
            <th style="background:#4472c4;color:#fff;padding:6px 8px;font-size:12px;border:1px solid #ccc;white-space:nowrap;">${dayHeader}</th>
            ${periodHeaders}
          </tr>
        </thead>
        <tbody>${bodyRows}</tbody>
      </table>
    </div>`;
};

// แปลงตารางเป็นรูปแล้ววางลง A4 แนวนอน — jsPDF โหลดฟอนต์ไทยเองไม่ได้ การแคปเป็น
// รูปจึงเป็นทางเดียวที่ได้ทั้งภาษาไทยและอาหรับในใบเดียวกัน
export const exportTimetablePdf = async ({ filename, ...doc }) => {
  if (!doc.periods?.length) return;
  const { default: jsPDF } = await import("jspdf");

  const el = document.createElement("div");
  el.style.cssText = "position:fixed;left:-99999px;top:0;background:#fff;";
  el.innerHTML = timetableHtml(doc);
  document.body.appendChild(el);
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(el, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pw = 841.89; // A4 แนวนอน (pt)
    const ph = 595.28;
    const imgH = (canvas.height * pw) / canvas.width;
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    if (imgH <= ph) pdf.addImage(imgData, "PNG", 0, 0, pw, imgH);
    else pdf.addImage(imgData, "PNG", 0, 0, pw * (ph / imgH), ph);
    pdf.save(filename);
  } finally {
    document.body.removeChild(el);
  }
};

// สั่งพิมพ์ผ่าน iframe ซ่อน — เหตุผลเดียวกับ teaching/timetableGrid.js: window.open
// โดนตัวบล็อกป๊อปอัพ ส่วน window.print() ตรงๆ จะติดเมนูกับปุ่มของหน้าจอไปด้วย และ
// เขียนด้วย document.write เพราะเป็น synchronous เอกสารพร้อมทันทีที่ close() คืนค่า
// ไม่ต้องรอ event load ซึ่งเป็นต้นเหตุที่พิมพ์ออกมาเป็นกระดาษเปล่า
export const printTimetableDoc = (doc) => {
  if (!doc.periods?.length) return;
  const body = timetableHtml(doc);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  // ต้องมีขนาดจริง ห้าม 0×0 หรือ display:none — เนื้อหาที่ไม่ถูกจัดหน้าจะพิมพ์ออกมาเปล่า
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1024px;height:800px;border:0;visibility:hidden;";
  document.body.appendChild(iframe);

  const frameDoc = iframe.contentWindow.document;
  frameDoc.open();
  frameDoc.write(`<!doctype html><html lang="${doc.rtl ? "ms" : "th"}" dir="${doc.rtl ? "rtl" : "ltr"}"><head><meta charset="utf-8">
    <title>${doc.title}</title>
    <style>@page { size: A4 landscape; margin: 10mm; } body { margin: 0; }</style>
    </head><body>${body}</body></html>`);
  frameDoc.close();

  iframe.contentWindow.focus();
  iframe.contentWindow.print();

  // รอให้กล่องพิมพ์ทำงานเสร็จก่อนเก็บ iframe ทิ้ง — ลบทันทีบางเบราว์เซอร์จะยกเลิกงานพิมพ์
  setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1000);
};
